import { formatDate, formatDateTime } from '../../../utils/dateFormat';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serviceAgentService, DelegationDto, AuditLogDto, PageResponse } from '../../../services/serviceAgent.service';
import { delegationService } from '../../../services/delegation.service';
import { DelegationScope } from '../../../models/DelegationDto';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { PersonSpFilterBanner, guidEq } from '../../../components/PersonSpFilterBanner/PersonSpFilterBanner';
import { investorService, PersonAccountGroup } from '../../../services/investor.service';
import { profileService, ServiceProviderDto } from '../../../services/profile.service';
import './DelegationManagement.scss';

/** Scope options with helper text — mirrors the Investor_COnsent_SA.html design. */
const SCOPE_OPTIONS: { value: DelegationScope; label: string; description: string }[] = [
  { value: 'VIEW_ONLY', label: 'View Only', description: 'Can only view your information — no edits.' },
  { value: 'CKYC_ONLY', label: 'CKYC Only', description: 'CKYC / KRA forms, passport and tax info only.' },
  { value: 'ONBOARDING_ONLY', label: 'Onboarding Only', description: 'Bank details, nomination and other information only.' },
  { value: 'FULL_ONBOARDING', label: 'Full Onboarding', description: 'Complete access to all onboarding modules.' },
];
const SCOPE_LABELS: Record<string, string> = Object.fromEntries(SCOPE_OPTIONS.map(o => [o.value, o.label]));

const STATUS_BADGE: Record<string, string> = {
  PENDING: 'bg-warning text-dark',
  ACTIVE: 'bg-success',
  REVOKED: 'bg-secondary',
  REJECTED: 'bg-danger',
};

interface ConsentForm {
  scope: DelegationScope;
  canViewProfile: boolean;
  canEditKyc: boolean;
  canUploadDocuments: boolean;
  canSubmitForms: boolean;
  consentGiven: boolean;
}

export const DelegationManagement: React.FC = () => {
  const navigate = useNavigate();
  const [delegations, setDelegations] = useState<DelegationDto[]>([]);
  // PM2: Accounts (person) × Service Provider filters — same banner as My Documents.
  const [personGroups, setPersonGroups] = useState<PersonAccountGroup[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderDto[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | 'ALL'>('ALL');
  const [selectedSpRef, setSelectedSpRef] = useState<string>('ALL');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<ConsentForm | null>(null);
  // Delegation-grant gate: null = unknown (never blocks; the backend is the backstop).
  const [kycComplete, setKycComplete] = useState<boolean | null>(null);

  // ── Activity (transparency log: what the agent did on my account) ─────────
  const [activeTab, setActiveTab] = useState<'consents' | 'activity'>('consents');
  const [activity, setActivity] = useState<PageResponse<AuditLogDto> | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(0);

  useEffect(() => {
    fetchDelegations();
    // PM2 filter data — best-effort; failures just leave the dropdowns at All.
    investorService.getPersonAccounts()
      .then((g) => setPersonGroups(g || []))
      .catch(() => setPersonGroups([]));
    profileService.getServiceProviders()
      .then((sp) => setServiceProviders(sp || []))
      .catch(() => setServiceProviders([]));
    delegationService.getKycGate()
      .then(g => setKycComplete(g.kycComplete))
      .catch(() => setKycComplete(null)); // unknown → don't block; backend enforces anyway
  }, []);

  useEffect(() => {
    if (activeTab === 'activity') fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activityPage]);

  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      setActivity(await serviceAgentService.getMyAgentActivity(activityPage, 20));
    } catch {
      toast.error('Failed to load agent activity');
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchDelegations = async (keepSelection = false) => {
    try {
      const data = await serviceAgentService.getMyDelegations();
      setDelegations(data);
      if (!keepSelection && data.length > 0) {
        selectDelegation(data[0]);
      } else if (keepSelection && selectedId != null) {
        const refreshed = data.find(d => d.id === selectedId);
        if (refreshed) selectDelegation(refreshed);
      }
    } catch {
      toast.error('Failed to load delegations');
    } finally {
      setLoading(false);
    }
  };

  const selectDelegation = (d: DelegationDto) => {
    setSelectedId(d.id);
    setForm({
      scope: d.scope as DelegationScope,
      canViewProfile: d.canViewProfile ?? true,
      canEditKyc: d.canEditKyc ?? false,
      canUploadDocuments: d.canUploadDocuments ?? false,
      canSubmitForms: d.canSubmitForms ?? false,
      // Active delegations were already consented; pending requires a fresh tick.
      consentGiven: d.status === 'ACTIVE',
    });
  };

  const selected = delegations.find(d => d.id === selectedId) || null;
  const isViewOnly = form?.scope === 'VIEW_ONLY';
  const editable = selected != null && (selected.status === 'PENDING' || selected.status === 'ACTIVE');

  const setField = (patch: Partial<ConsentForm>) => setForm(f => (f ? { ...f, ...patch } : f));

  const handleSave = async () => {
    if (!selected || !form) return;
    if (selected.status === 'PENDING' && !form.consentGiven) {
      toast.warn('Please tick the consent box to authorise this Service Agent.');
      return;
    }
    const customizations = {
      scope: form.scope,
      canViewProfile: form.canViewProfile,
      canEditKyc: isViewOnly ? false : form.canEditKyc,
      canUploadDocuments: isViewOnly ? false : form.canUploadDocuments,
      canSubmitForms: isViewOnly ? false : form.canSubmitForms,
    };
    setProcessing(selected.id);
    try {
      if (selected.status === 'PENDING') {
        await delegationService.acceptDelegation(selected.id, customizations);
        toast.success('Consent saved — the Service Agent can now act on your behalf.');
      } else {
        await delegationService.updateDelegation(selected.id, customizations);
        toast.success('Permissions updated.');
      }
      await fetchDelegations(true);
    } catch (error: any) {
      const msg: string = error?.response?.data?.message || '';
      if (msg.includes('KYC_INCOMPLETE')) {
        setKycComplete(false);
        toast.warn('Complete your KYC documents before authorizing a Service Agent.');
      } else {
        toast.error(msg || 'Failed to save consent');
      }
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!globalThis.confirm(`Reject the assignment from ${selected.serviceAgentName}?`)) return;
    setProcessing(selected.id);
    try {
      await delegationService.rejectDelegation(selected.id, 'Declined by investor');
      toast.info(`Assignment rejected: ${selected.serviceAgentName}`);
      await fetchDelegations();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const handleRevoke = async () => {
    if (!selected) return;
    if (!globalThis.confirm(`Revoke access for ${selected.serviceAgentName}? They will immediately lose access to your account.`)) return;
    setProcessing(selected.id);
    try {
      await serviceAgentService.revokeDelegation(selected.id);
      toast.success('Consent revoked.');
      await fetchDelegations();
    } catch {
      toast.error('Failed to revoke');
    } finally {
      setProcessing(null);
    }
  };

  const fmtDate = (d?: string) => (d ? formatDate(d) : null);
  const fmtDateTime = (d?: any) => {
    if (!d) return '—';
    // Backend may serialize LocalDateTime as an ISO string OR a [y,mo,day,h,mi,s] array.
    if (Array.isArray(d)) {
      const [y, mo = 1, day = 1, h = 0, mi = 0, s = 0] = d;
      return formatDateTime(new Date(y, mo - 1, day, h, mi, s));
    }
    return formatDateTime(d);
  };

  // PM2: fail-open AND filter — a delegation is excluded only when POSITIVELY attributed
  // to a different person/SP (identity-wide rows, null spRef, pre-person rows never hide).
  const selfPersonId = personGroups[0]?.personId ?? null;
  const filteredDelegations = delegations.filter((d) => {
    if (selectedPersonId !== 'ALL') {
      const pid = d.personId ?? selfPersonId;
      if (pid !== null && pid !== selectedPersonId) return false;
    }
    if (selectedSpRef !== 'ALL' && d.spRef && !guidEq(d.spRef, selectedSpRef)) return false;
    return true;
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="layout-wrapper">
      <main className="container-fluid dashboard-container-main">
        <div className="delegation-management">

          {/* PM2: dashboard-style banner with the Accounts × SP filters */}
          <div className="mb-3">
            <PersonSpFilterBanner
              title="My Permissions"
              personGroups={personGroups}
              serviceProviders={serviceProviders}
              selectedPersonId={selectedPersonId}
              selectedSpRef={selectedSpRef}
              onPersonChange={setSelectedPersonId}
              onSpChange={setSelectedSpRef}
            />
          </div>

          {/* Tabs: manage consents  |  see what the agent did on my account */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'consents' ? 'active' : ''}`}
                onClick={() => setActiveTab('consents')}
              >
                <i className="bi bi-shield-check me-1"></i> Consents
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                <i className="bi bi-clock-history me-1"></i> Activity
              </button>
            </li>
          </ul>

          {activeTab === 'consents' ? (
          <div className="row g-3">
            {/* ── Left: Existing Consents ─────────────────────────────── */}
            <div className="col-lg-5">
              <div className="card h-100">
                <div className="card-header bg-white fw-bold">Assigned Service Agent</div>
                <div className="card-body p-2">
                  {filteredDelegations.length === 0 && (
                    <div className="text-center text-muted small py-4">
                      {delegations.length > 0
                        ? 'No Service Agent matches the selected filters.'
                        : (<>No Service Agent assigned yet.<br />
                          Your Service Provider will assign a Service Agent to assist you.</>)}
                    </div>
                  )}
                  {filteredDelegations.map(d => {
                    const perms = [
                      d.canViewProfile && 'View',
                      d.canEditKyc && 'Edit',
                      d.canUploadDocuments && 'Upload',
                      d.canSubmitForms && 'Submit',
                    ].filter(Boolean).join(', ');
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => selectDelegation(d)}
                        className={`w-100 text-start border rounded p-2 mb-2 d-block ${selectedId === d.id ? 'border-primary' : 'border-light-subtle'}`}
                        style={{ background: selectedId === d.id ? '#f1f5f9' : '#fff', cursor: 'pointer' }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <span className="fw-bold text-dark">{d.serviceAgentName || 'Service Agent'}</span>
                          <span className={`badge ${STATUS_BADGE[d.status || ''] || 'bg-secondary'}`}>
                            {d.status || (d.isActive ? 'ACTIVE' : '—')}
                          </span>
                        </div>
                        {d.serviceAgentEmail && <div className="small text-muted">{d.serviceAgentEmail}</div>}
                        {d.serviceAgentMobile && <div className="small text-muted">📱 {d.serviceAgentMobile}</div>}
                        {d.serviceAgentCode && <div className="small text-muted">Code: {d.serviceAgentCode}</div>}
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {/* Account chip: whose journey this delegation covers (person layer) */}
                          {(d.personName || personGroups[0]?.displayName) && (
                            <span className="badge border" style={{ background: '#fdf6e2', color: '#b0842b', borderColor: '#b0842b40' }}>
                              <i className="bi bi-person me-1"></i>
                              {d.personName || personGroups[0]?.displayName}
                            </span>
                          )}
                          <span className="badge bg-light text-dark border">{SCOPE_LABELS[d.scope] || d.scope}</span>
                          {perms && <span className="badge bg-light text-dark border">{perms}</span>}
                          {(d.productName || d.relatedRef) ? (
                            <span
                              className="badge border"
                              style={{ background: '#eef6ff', color: '#1e40af', borderColor: '#bfdbfe' }}
                              title={d.serviceProviderName ? `Service Provider: ${d.serviceProviderName}` : undefined}
                            >
                              🔒 {d.productName || 'Primary account'}
                            </span>
                          ) : (
                            <span className="badge bg-light text-dark border" title="Applies to all of your products/accounts">
                              All accounts
                            </span>
                          )}
                        </div>
                        <div className="small text-muted mt-1">
                          Valid {fmtDate(d.validFrom) || 'immediately'} → {fmtDate(d.validTo) || 'no expiry'}
                        </div>
                        {d.notes && <div className="small text-muted mt-1"><em>{d.notes}</em></div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Right: Grant or Update Consent ──────────────────────── */}
            <div className="col-lg-7">
              <div className="card h-100">
                <div className="card-header bg-white fw-bold">Grant or Update Consent</div>
                <div className="card-body">
                  {!selected || !form ? (
                    <div className="text-center text-muted small py-5">
                      Select a Service Agent on the left to view or change their permissions.
                    </div>
                  ) : (
                    <>
                      {/* Agent details */}
                      <div className="mb-3">
                        <div className="fw-bold">{selected.serviceAgentName}</div>
                        <div className="small text-muted">{selected.serviceAgentEmail}</div>
                        {selected.serviceAgentMobile && <div className="small text-muted">📱 {selected.serviceAgentMobile}</div>}
                        <div className="small text-muted">Code: {selected.serviceAgentCode}</div>
                        <div className="small mt-1">
                          <span className="text-muted">Authorised for: </span>
                          <strong>
                            {selected.productName
                              || (selected.relatedRef ? 'Primary account' : 'All accounts / all products')}
                          </strong>
                          {selected.serviceProviderName && (
                            <span className="text-muted"> · {selected.serviceProviderName}</span>
                          )}
                        </div>
                      </div>

                      {/* Scope + helper text */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold mb-1">Access scope</label>
                        <select
                          className="form-select"
                          value={form.scope}
                          disabled={!editable}
                          onChange={(e) => setField({ scope: e.target.value as DelegationScope })}
                        >
                          {SCOPE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <div className="form-text">
                          {SCOPE_OPTIONS.find(o => o.value === form.scope)?.description}
                        </div>
                      </div>

                      {/* Permissions */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold mb-1">Permissions</label>
                        {[
                          { key: 'canViewProfile', label: 'Can view profile & documents', always: true },
                          { key: 'canEditKyc', label: 'Can edit KYC / personal information', always: false },
                          { key: 'canUploadDocuments', label: 'Can upload documents on my behalf', always: false },
                          { key: 'canSubmitForms', label: 'Can submit forms on my behalf', always: false },
                        ].map(p => (
                          <div className="form-check" key={p.key}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={p.key}
                              checked={(form as any)[p.key]}
                              disabled={!editable || (!p.always && isViewOnly)}
                              onChange={(e) => setField({ [p.key]: e.target.checked } as any)}
                            />
                            <label className="form-check-label small" htmlFor={p.key}>{p.label}</label>
                          </div>
                        ))}
                        {isViewOnly && (
                          <div className="form-text text-warning">
                            View Only — edit, upload and submit are disabled.
                          </div>
                        )}
                      </div>

                      {/* Validity + reference (read-only) */}
                      <div className="row mb-3">
                        <div className="col-6">
                          <div className="small text-muted">Valid from</div>
                          <div className="small">{fmtDate(selected.validFrom) || 'Immediately'}</div>
                        </div>
                        <div className="col-6">
                          <div className="small text-muted">Valid until</div>
                          <div className="small">{fmtDate(selected.validTo) || 'No expiry'}</div>
                        </div>
                      </div>
                      {selected.notes && (
                        <div className="alert alert-secondary small py-2">
                          <strong>Reference / Notes:</strong> {selected.notes}
                        </div>
                      )}

                      {/* Delegation-grant gate: documents (OCR) must be done before authorizing */}
                      {selected.status === 'PENDING' && kycComplete === false && (
                        <div className="alert alert-warning small py-2 d-flex justify-content-between align-items-center gap-2">
                          <span>
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Complete your KYC documents first — your Service Agent will then work
                            with the data extracted from them.
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning text-nowrap"
                            // Journey-scoped (2026-09-05): this delegation belongs to ONE journey —
                            // open THAT journey's Smart Upload, not the identity-level docs page.
                            onClick={() => navigate(selected.journeyId
                              ? `/investor/documents-center?mode=upload&journeyId=${encodeURIComponent(selected.journeyId)}`
                              : '/investor/documents-center?mode=upload')}
                          >
                            Go to Documents →
                          </button>
                        </div>
                      )}

                      {/* Consent statement (pending only requires a fresh tick) */}
                      {selected.status === 'PENDING' && (
                        <div className="form-check mb-3 p-2 border rounded bg-light">
                          <input
                            className="form-check-input ms-0 me-2"
                            type="checkbox"
                            id="consentGiven"
                            checked={form.consentGiven}
                            onChange={(e) => setField({ consentGiven: e.target.checked })}
                          />
                          <label className="form-check-label small" htmlFor="consentGiven">
                            I authorise <strong>{selected.serviceAgentName}</strong> to access my data and act on my
                            behalf within the scope and permissions selected above. All their actions are logged, and
                            I can revoke this at any time.
                          </label>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="d-flex gap-2">
                        {selected.status === 'PENDING' && (
                          <>
                            <button
                              className="btn btn-success flex-fill"
                              onClick={handleSave}
                              disabled={processing === selected.id || kycComplete === false}
                              title={kycComplete === false ? 'Complete your KYC documents to authorize this agent' : undefined}
                            >
                              {processing === selected.id ? 'Saving…' : 'Confirm & Save Consent'}
                            </button>
                            <button className="btn btn-outline-danger" onClick={handleReject} disabled={processing === selected.id}>
                              Reject
                            </button>
                          </>
                        )}
                        {selected.status === 'ACTIVE' && (
                          <>
                            <button className="btn btn-primary flex-fill" onClick={handleSave} disabled={processing === selected.id}>
                              {processing === selected.id ? 'Saving…' : 'Save Changes'}
                            </button>
                            <button className="btn btn-outline-danger" onClick={handleRevoke} disabled={processing === selected.id}>
                              Revoke Consent
                            </button>
                          </>
                        )}
                        {(selected.status === 'REVOKED' || selected.status === 'REJECTED') && (
                          <div className="text-muted small">This consent is {selected.status?.toLowerCase()} and can no longer be changed.</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          ) : (
            /* ── Activity: what the Service Agent did on my account ───── */
            <div className="card">
              <div className="card-header bg-white fw-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-clock-history me-2"></i>Service Agent Activity on Your Account</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={fetchActivity} disabled={activityLoading} title="Refresh">
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
              <div className="card-body p-0">
                {activityLoading ? (
                  <div className="py-5"><LoadingSpinner /></div>
                ) : !activity || activity.content.length === 0 ? (
                  <div className="text-center text-muted small py-5">
                    No agent activity yet.<br />
                    When a Service Agent views or updates your account, every action shows up here.
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0 small">
                        <thead className="table-light">
                          <tr>
                            <th>Date / Time</th>
                            <th>Service Agent</th>
                            <th>Action</th>
                            <th>Category</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activity.content.map(log => (
                            <tr key={log.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(log.createdAt)}</td>
                              <td>{log.serviceAgentName || '—'}</td>
                              <td>{(log.actionType || '').replace(/_/g, ' ')}</td>
                              <td><span className="badge bg-light text-dark border">{log.actionCategory}</span></td>
                              <td>
                                {log.success
                                  ? <span className="badge bg-success">Success</span>
                                  : <span className="badge bg-danger" title={log.errorMessage}>Failed</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {activity.totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center p-2 border-top">
                        <button className="btn btn-sm btn-outline-secondary" disabled={activityPage === 0} onClick={() => setActivityPage(p => p - 1)}>← Prev</button>
                        <span className="small text-muted">Page {activityPage + 1} of {activity.totalPages}</span>
                        <button className="btn btn-sm btn-outline-secondary" disabled={activityPage >= activity.totalPages - 1} onClick={() => setActivityPage(p => p + 1)}>Next →</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DelegationManagement;
